import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePostInput,
  ListPostsQuery,
  Paginated,
  PostDetail,
  PostListItem,
  PublicTrendingItem,
  PublishPostInput,
  RevisionDetail,
  RevisionListItem,
  SetViewCountInput,
  UpdatePostInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

/** Trending posts (top by views over a trailing window), backing the
 *  dashboard "Top viewed" card. Reads the public endpoint (same-origin via
 *  the Pages proxy). Refetches at the pageview-flush cadence. */
export const trendingPostsQueryOptions = (window: '1d' | '7d' | '30d' = '7d', limit = 8) =>
  queryOptions({
    queryKey: ['posts', 'trending', window, limit] as const,
    queryFn: () =>
      api<{ items: PublicTrendingItem[] }>(
        `/api/public/posts/trending?window=${window}&limit=${limit}`,
      ),
    staleTime: 5 * 60_000,
  });

function qs(filter: Partial<ListPostsQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      params.set(k, v.join(','));
    } else {
      params.set(k, String(v));
    }
  }
  return params.toString();
}

/* ---------- query options ---------- */

export const postsListQueryOptions = (filter: Partial<ListPostsQuery>) =>
  queryOptions({
    queryKey: ['posts', 'list', filter] as const,
    queryFn: () => api<Paginated<PostListItem>>(`/api/admin/posts?${qs(filter)}`),
  });

export const postDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['posts', 'detail', id] as const,
    queryFn: () => api<{ post: PostDetail }>(`/api/admin/posts/${id}`).then((d) => d.post),
  });

export const postRevisionsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['posts', 'revisions', id] as const,
    queryFn: () =>
      api<{ revisions: RevisionListItem[] }>(`/api/admin/posts/${id}/revisions`).then(
        (d) => d.revisions,
      ),
  });

export const revisionDetailQueryOptions = (postId: string, revisionId: string) =>
  queryOptions({
    queryKey: ['posts', 'revisions', postId, revisionId] as const,
    queryFn: () =>
      api<{ revision: RevisionDetail }>(`/api/admin/posts/${postId}/revisions/${revisionId}`).then(
        (d) => d.revision,
      ),
    staleTime: Infinity, // revisions are immutable
  });

/* ---------- mutations ---------- */

export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePostInput) => {
      const data = await api<{ post: PostDetail }>('/api/admin/posts', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.post;
    },
    onSuccess: (post) => {
      qc.setQueryData(postDetailQueryOptions(post.id).queryKey, post);
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      // postCount on /tags + /categories admin pages uses correlated
      // subqueries — bust their cache so the count reflects the post's
      // freshly-attached tagIds/categoryIds.
      void qc.invalidateQueries({ queryKey: ['taxonomies'] });
      toast.success('สร้างบทความเรียบร้อย');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function useUpdatePost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePostInput) => {
      const data = await api<{ post: PostDetail }>(`/api/admin/posts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.post;
    },
    onSuccess: (post) => {
      qc.setQueryData(postDetailQueryOptions(post.id).queryKey, post);
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['posts', 'revisions', post.id] });
      // Same as useCreatePost — tagIds/categoryIds may have changed in
      // the patch; refresh /tags + /categories postCount.
      void qc.invalidateQueries({ queryKey: ['taxonomies'] });
      toast.success('บันทึกเรียบร้อย');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/posts/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['posts', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('ย้ายเข้าถังขยะแล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function useRestorePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/posts/${id}/restore`, { method: 'POST' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['posts', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('กู้คืนบทความแล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Mint a signed preview token for the given post. Returns the token
 *  + expiry so the caller can render the shareable URL. */
/**
 * Live slug availability check used by the post form. Hits the
 * `_slug-check` endpoint that returns `{ valid, available, reason }`
 * so the form can render distinct hints for "invalid shape" vs
 * "already in use" vs "ok".
 *
 * Always returns a 200; never throws on duplicates. The `enabled`
 * gate keeps it idle when the slug is empty.
 */
export interface SlugCheckResult {
  valid: boolean;
  available: boolean;
  reason: 'empty' | 'invalid' | null;
}

export function useSlugCheck(slug: string, excludeId: string | undefined) {
  return useQuery({
    queryKey: ['posts', 'slug-check', slug, excludeId ?? ''] as const,
    queryFn: () => {
      const params = new URLSearchParams({ slug });
      if (excludeId) params.set('excludeId', excludeId);
      return api<SlugCheckResult>(`/api/admin/posts/_slug-check?${params.toString()}`);
    },
    enabled: slug.length > 0,
    staleTime: 30_000,
  });
}

export function useGeneratePreviewToken() {
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await api<{ token: string; expiresAt: string }>(
        `/api/admin/posts/${id}/preview-token`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      return data;
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Clone a post as a fresh draft. Returns the new post detail so the
 *  caller can navigate to the editor for the copy. */
export function useDuplicatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await api<{ post: PostDetail }>(`/api/admin/posts/${id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return data.post;
    },
    onSuccess: (post) => {
      qc.setQueryData(postDetailQueryOptions(post.id).queryKey, post);
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      // Duplicate copies tagIds + categoryIds onto the new post →
      // post_tags + post_categories grow → bust /tags + /categories cache.
      void qc.invalidateQueries({ queryKey: ['taxonomies'] });
      toast.success('สร้างสำเนาบทความแล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Hard-delete a soft-deleted post. The API rejects with 404 if the
 *  post is not already in trash, so a misclick on a live post is a
 *  no-op (and toast surfaces the error). */
export function usePurgePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ ok: true }>(`/api/admin/posts/${id}/purge`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['posts', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('ลบถาวรแล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Archive a single post — flips status to 'archived'. Inverse of
 *  useUnarchivePost. Both reuse the same id-bound mutation shape as
 *  useUpdatePost / useUnpublishPost. */
export function useArchivePost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api<{ ok: true }>(`/api/admin/posts/${id}/archive`, { method: 'POST' });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('เก็บถาวรแล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function useUnarchivePost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api<{ ok: true }>(`/api/admin/posts/${id}/unarchive`, { method: 'POST' });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['posts', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('นำกลับเป็น draft แล้ว');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function usePublishPost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PublishPostInput = {}) => {
      const data = await api<{ post: PostDetail }>(`/api/admin/posts/${id}/publish`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.post;
    },
    onSuccess: (post) => {
      qc.setQueryData(postDetailQueryOptions(post.id).queryKey, post);
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('เผยแพร่บทความเรียบร้อย');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export function useUnpublishPost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await api<{ post: PostDetail }>(`/api/admin/posts/${id}/unpublish`, {
        method: 'POST',
      });
      return data.post;
    },
    onSuccess: (post) => {
      qc.setQueryData(postDetailQueryOptions(post.id).queryKey, post);
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      toast.success('ถอนการเผยแพร่เรียบร้อย');
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Toggle a single boolean flag on a post (isSticky / isBreaking /
 *  isFeatured). Optimistically updates the cached list so the icon
 *  flips instantly; reverts on error.
 *
 *  Re-implemented separately from useUpdatePost because: (a) per-row
 *  toast "บันทึกเรียบร้อย" would be noisy on a frequent quick-toggle,
 *  (b) optimistic update needs to know which flag changed. */
export function useTogglePostFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      flag: 'isSticky' | 'isBreaking' | 'isFeatured';
      value: boolean;
    }) => {
      const { id, flag, value } = input;
      await api(`/api/admin/posts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [flag]: value }),
      });
      return input;
    },
    onMutate: async ({ id, flag, value }) => {
      // Optimistic update: walk every cached posts-list query and flip
      // the matching row. Keeps the icon snappy without a refetch.
      await qc.cancelQueries({ queryKey: ['posts', 'list'] });
      const snapshot = qc.getQueriesData<{ items: PostListItem[] } | undefined>({
        queryKey: ['posts', 'list'],
      });
      for (const [key, prev] of snapshot) {
        if (!prev?.items) continue;
        qc.setQueryData(key, {
          ...prev,
          items: prev.items.map((p) => (p.id === id ? { ...p, [flag]: value } : p)),
        });
      }
      return { snapshot };
    },
    onError: (_e, _vars, ctx) => {
      // Roll back every snapshot we captured.
      if (ctx?.snapshot) {
        for (const [key, prev] of ctx.snapshot) {
          qc.setQueryData(key, prev);
        }
      }
      toast.error('สลับสถานะไม่สำเร็จ');
    },
    onSuccess: ({ flag, value }) => {
      // Refetch the detail too in case admin had it open in another
      // tab — list is already optimistic so no refetch there.
      void qc.invalidateQueries({ queryKey: ['posts', 'detail'] });
      const labels: Record<typeof flag, string> = {
        isSticky: 'ปักหมุด',
        isBreaking: 'ข่าวด่วน',
        isFeatured: 'แนะนำ',
      };
      toast.success(`${value ? labels[flag] : 'เลิก ' + labels[flag]}แล้ว`);
    },
  });
}

/** Manually set/bump a post's all-time view_count (super_admin only).
 *  Patches the matching row in every cached list so the new count
 *  shows immediately, then invalidates to reconcile with the server. */
export function useSetViewCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & SetViewCountInput) => {
      return api<{ id: string; viewCount: number }>(`/api/admin/posts/${id}/view-count`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      const snapshot = qc.getQueriesData<{ items: PostListItem[] } | undefined>({
        queryKey: ['posts', 'list'],
      });
      for (const [key, prev] of snapshot) {
        if (!prev?.items) continue;
        qc.setQueryData(key, {
          ...prev,
          items: prev.items.map((p) =>
            p.id === data.id ? { ...p, viewCount: data.viewCount } : p,
          ),
        });
      }
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['posts', 'detail', data.id] });
      toast.success(`ตั้งยอดวิวเป็น ${data.viewCount.toLocaleString()} แล้ว`);
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Bulk add/remove CATEGORIES across many posts in one request.
 *  Symmetric to useBulkTagPosts; separate hook so toasts can be
 *  specific ("+1 หมวด" vs "+1 แท็ก") and the endpoints stay
 *  un-overloaded. */
export function useBulkCategoryPosts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      addCategoryIds?: string[];
      removeCategoryIds?: string[];
    }) => {
      return api<{
        fulfilled: number;
        rejected: number;
        addedCount: number;
        removedCount: number;
      }>(`/api/admin/posts/_bulk-category`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['posts', 'detail'] });
      // post_categories rows added/removed → /categories postCount stale.
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'categories'] });
      const parts: string[] = [];
      if (result.addedCount > 0) parts.push(`+${result.addedCount} หมวด`);
      if (result.removedCount > 0) parts.push(`-${result.removedCount} หมวด`);
      const summary = parts.length > 0 ? parts.join(' / ') : 'ไม่มีการเปลี่ยนแปลง';
      if (result.rejected === 0) {
        toast.success(`อัปเดต ${result.fulfilled} บทความ (${summary})`);
      } else {
        toast.warning(
          `อัปเดต ${result.fulfilled} สำเร็จ · ${result.rejected} ล้มเหลว (${summary})`,
        );
      }
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/** Bulk add/remove tags across many posts in one request. Mirrors
 *  the backend bulkTagPostsSchema shape; returns aggregated counts
 *  so the toast can show what actually changed. */
export function useBulkTagPosts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; addTagIds?: string[]; removeTagIds?: string[] }) => {
      return api<{
        fulfilled: number;
        rejected: number;
        addedCount: number;
        removedCount: number;
      }>(`/api/admin/posts/_bulk-tag`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['posts', 'detail'] });
      // post_tags rows added/removed → /tags postCount stale.
      void qc.invalidateQueries({ queryKey: ['taxonomies', 'tags'] });
      const parts: string[] = [];
      if (result.addedCount > 0) parts.push(`+${result.addedCount} แท็ก`);
      if (result.removedCount > 0) parts.push(`-${result.removedCount} แท็ก`);
      const summary = parts.length > 0 ? parts.join(' / ') : 'ไม่มีการเปลี่ยนแปลง';
      if (result.rejected === 0) {
        toast.success(`อัปเดต ${result.fulfilled} บทความ (${summary})`);
      } else {
        toast.warning(
          `อัปเดต ${result.fulfilled} สำเร็จ · ${result.rejected} ล้มเหลว (${summary})`,
        );
      }
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

export type BulkPostAction =
  | 'publish'
  | 'unpublish'
  | 'delete'
  | 'restore'
  | 'purge'
  | 'schedule'
  | 'archive'
  | 'unarchive';

const BULK_ACTION_LABEL: Record<BulkPostAction, string> = {
  publish: 'เผยแพร่',
  unpublish: 'ถอนการเผยแพร่',
  delete: 'ลบ',
  restore: 'กู้คืน',
  purge: 'ลบถาวร',
  schedule: 'ตั้งเวลาเผยแพร่',
  archive: 'เก็บถาวร',
  unarchive: 'เลิกเก็บถาวร',
};

export function useBulkPostAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      action,
      scheduledAt,
    }: {
      ids: string[];
      action: BulkPostAction;
      /** Required when action='schedule'; ignored otherwise. Same ISO
       *  string applied to every post in the batch — per-row times
       *  would need a different UX. */
      scheduledAt?: string;
    }) => {
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          if (action === 'delete') {
            await api(`/api/admin/posts/${id}`, { method: 'DELETE' });
          } else if (action === 'purge') {
            await api(`/api/admin/posts/${id}/purge`, { method: 'DELETE' });
          } else if (action === 'schedule') {
            // Same publish endpoint as immediate publish — the schema
            // treats scheduledAt as optional, presence triggers
            // schedule-mode (status='scheduled' until the cron flips it).
            await api(`/api/admin/posts/${id}/publish`, {
              method: 'POST',
              body: JSON.stringify({ scheduledAt }),
            });
          } else {
            await api(`/api/admin/posts/${id}/${action}`, {
              method: 'POST',
              body: JSON.stringify({}),
            });
          }
          return id;
        }),
      );
      const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
      const rejected = results.length - fulfilled;
      return { fulfilled, rejected, action };
    },
    onSuccess: ({ fulfilled, rejected, action }) => {
      void qc.invalidateQueries({ queryKey: ['posts', 'list'] });
      void qc.invalidateQueries({ queryKey: ['posts', 'detail'] });
      const label = BULK_ACTION_LABEL[action];
      if (rejected === 0) toast.success(`${label} ${fulfilled} บทความเรียบร้อย`);
      else toast.warning(`${label}สำเร็จ ${fulfilled} · ผิดพลาด ${rejected} (สิทธิ์ไม่พอ?)`);
    },
    onError: (e) => toast.error(postErrorMessage(e)),
  });
}

/* ---------- error mapping ---------- */

export function postErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'SLUG_CONFLICT':
      return 'slug นี้ถูกใช้แล้ว กรุณาเปลี่ยน';
    case 'POST_NOT_FOUND':
      return 'ไม่พบบทความที่ต้องการ';
    case 'FORBIDDEN':
      return 'คุณไม่มีสิทธิ์ดำเนินการนี้';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง';
    case 'UNAUTHORIZED':
      return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่';
    case 'ARTICLE_IN_USE':
      // Server's Thai message is already actionable ("ลบออกจาก group ก่อน")
      // — surface it verbatim so the toast names the blocking groups
      // via the error details panel if any caller renders it.
      return e.message;
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
